import {
  ADDRESS_AR,
  ADDRESS_EN,
  DEVELOPER_AR,
  DEVELOPER_EN,
  OPERATOR_AR,
  OPERATOR_EN,
  OWNER_AR,
  OWNER_EN,
  OWNER_PHONE,
  PRIVACY_EFFECTIVE,
  PRIVACY_VERSION,
  PRODUCT_AR,
  PRODUCT_EN,
  SUPPORT_EMAIL,
  TERMS_EFFECTIVE,
  TERMS_VERSION,
  WEBSITE,
} from './identity';

export type LegalLocale = 'ar' | 'en';

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

export interface LegalDocument {
  type: 'privacy_policy' | 'terms_of_use';
  version: string;
  effectiveDate: string;
  lastUpdated: string;
  title: Record<LegalLocale, string>;
  sections: Record<LegalLocale, LegalSection[]>;
}

export const privacyPolicy: LegalDocument = {
  type: 'privacy_policy',
  version: PRIVACY_VERSION,
  effectiveDate: PRIVACY_EFFECTIVE,
  lastUpdated: PRIVACY_EFFECTIVE,
  title: {
    ar: `سياسة الخصوصية — ${PRODUCT_AR}`,
    en: `Privacy Policy — ${PRODUCT_EN}`,
  },
  sections: {
    en: [
      {
        heading: 'Who we are',
        paragraphs: [
          `${PRODUCT_EN} (${PRODUCT_AR}) is operated by ${OPERATOR_EN}, whose owner and exclusive agent is ${OWNER_EN}. The registered address is ${ADDRESS_EN}. The owner telephone number ${OWNER_PHONE} is provided only as business identification and is not the official privacy, support, or account-deletion channel.`,
          `The official website is ${WEBSITE}. Privacy, data, support, and account-deletion requests should be sent to ${SUPPORT_EMAIL}.`,
          `${DEVELOPER_EN} developed the software. It is not the owner or operator of the ${PRODUCT_EN} service.`,
        ],
      },
      {
        heading: 'This policy describes current production behaviour',
        paragraphs: [
          `This Privacy Policy version ${PRIVACY_VERSION} (effective ${PRIVACY_EFFECTIVE}) describes how ${PRODUCT_EN} actually works today. It does not describe planned features as if they were already active.`,
          `${PRODUCT_EN} currently requires an account to use the service.`,
          'Version 1.1 adds push notifications, described below. They reach you only in a version of the mobile app that supports them, and only if you turn them on.',
        ],
      },
      {
        heading: 'Personal and account data',
        paragraphs: [
          'Account data may include: name; mobile phone number; email address where you provide one; the governorate you select manually; a profile image; account role or type; and authentication information.',
          'The mobile phone number may be used as your unique account and login identifier. Email is optional.',
          `${PRODUCT_EN} does not send SMS messages, does not use SMS one-time passwords, and does not track phone numbers beyond storing the number you register as your account identifier.`,
          'Passwords are stored only as secure password hashes. Original passwords cannot be read by staff.',
        ],
      },
      {
        heading: 'Vehicle data',
        paragraphs: [
          'You may store vehicle details, vehicle images, and maintenance or service-related information for vehicles you manage.',
          'You may associate a vehicle with a workshop. That association is an internal service relationship indicating that the vehicle is serviced by that workshop. It does not give the workshop unrestricted access to your private account or to every piece of vehicle information. Workshop staff can see only the vehicle and service information they are authorised to use for that service relationship.',
        ],
      },
      {
        heading: 'Workshops and stores',
        paragraphs: [
          `${PRODUCT_EN} includes a commercial directory of workshops and stores. Published business information may include the business name, description, governorate, area, textual address, working hours, services, business phone, WhatsApp contact if provided, and other business information the business intentionally publishes.`,
          'This is a business directory. The owner’s private profile is not published as public directory content.',
        ],
      },
      {
        heading: 'Location',
        paragraphs: [
          `${PRODUCT_EN} does not collect or request your geographic or GPS location. You choose a governorate manually. Workshop and store discovery is based on that selection, not on device location services.`,
        ],
      },
      {
        heading: 'Photos and camera',
        paragraphs: [
          'The app may allow you to upload a profile image, vehicle images, and product or inventory images; take a photo where the device supports it; and scan QR codes or barcodes.',
          'Camera and photo access is used only for those functions. The app prefers the system photo picker. It does not request broad, standing access to your entire photo library.',
        ],
      },
      {
        heading: 'Push notifications',
        paragraphs: [
          `If you turn them on, the ${PRODUCT_EN} mobile app sends push notifications about things that happen on your account. They are operational service notifications, not advertising. Push notifications start only when you choose to turn them on in the app; where your device requires permission, the app asks for it at that moment. You can turn them off at any time in your device settings.`,
          'Push notifications currently cover: a vehicle transfer being completed, or a vehicle being moved to or from your account by support; a service recorded or an invoice issued for your vehicle by a workshop; a decision on your workshop or store (approval, rejection or suspension); status changes of your orders; money received in your internal wallet; your marketplace listings being published, rejected, suspended or closed after a transfer, and enquiries about them; and messages the CarCarNa team writes to your account individually. Every push notification also appears in the notification list inside the app, and some types can be switched off in the app’s notification preferences.',
          'To deliver them, the app obtains a push token for your device and sends it to our servers with the device platform (iOS or Android) and the app version. We store the token linked to your account and to the session you are signed in with, so that notifications reach the right person on the right device. We do not collect advertising identifiers or hardware identifiers for this.',
          'A push notification contains a short title and text, and a small amount of technical information the app uses to open the related screen, such as the notification’s number and the number of the related vehicle, order or invoice. The text may name a vehicle (make, model and year), a workshop or store, an order or invoice number, or the reason given for a decision. For wallet transfers, the notification does not show the amount or the other person’s name; those details appear only inside the app.',
          'A device stops receiving push notifications for your account when you sign out on it, when you are signed out of all devices, when your password is reset, when your account is disabled or deleted, or when the session expires. If a push service reports that a device can no longer receive notifications, for example because the app was removed, we stop sending to it. If signing out cannot reach our servers, the app completes it later, and always before anyone else signs in on that device.',
          'For each push notification we keep delivery information needed to diagnose problems: whether it was accepted and passed to Apple or Google, the platform, and any error reported. It is kept with the notification and deleted with it when your account is deleted.',
        ],
      },
      {
        heading: 'Analytics, advertising, and tracking',
        paragraphs: [
          `${PRODUCT_EN} does not currently use analytics SDKs, advertising tracking, behavioural tracking, advertising SDKs, Meta Pixel, Google Analytics, or Firebase Analytics.`,
          'Push notifications do not change this: the services below are used only to deliver them.',
        ],
      },
      {
        heading: 'Service providers',
        paragraphs: [
          'Transactional email (email verification where applicable, and password recovery) is sent through Mailgun as a service provider / processor. Mailgun processes the email address and the content of those transactional messages solely to deliver them.',
          'Over-the-air updates for the native mobile app are delivered by Expo (EAS Update). After launch the app may contact the Expo update service to download an update. Expo processes ordinary request metadata such as IP address and platform or runtime information for that purpose. The public website does not use Expo.',
          'Push notifications are delivered through the Expo push notification service, which passes them to Apple Push Notification service (APNs) on iOS and to Firebase Cloud Messaging (FCM), a Google service, on Android. These providers process the push token, the notification’s title, text and technical information, and the routing information needed to deliver it. To issue a push token, Expo also receives the token Apple or Google assigns to the app on your device, an installation identifier generated by the app, and the app’s identifier. On Android, the messaging component in the app registers the device with Google to receive messages. Firebase Cloud Messaging is used only to deliver notifications; it is not Firebase Analytics.',
          `${PRODUCT_EN} does not currently use Telegram for notifications or account linking.`,
          'Backend data is hosted in Germany using STRATO infrastructure. Personal data is therefore processed and stored outside Syria. We do not make unsupported legal guarantees about cross-border transfers beyond describing this hosting arrangement.',
        ],
      },
      {
        heading: 'Sharing',
        paragraphs: [
          `${PRODUCT_EN} does not sell users’ personal data.`,
          'Personal data may be accessed or shared only as needed to provide the requested service, to run authorised business workflows, with service providers or processors, or to meet legal or security obligations.',
          'Profile pictures are not generally public. A small profile image may appear in legitimate product workflows such as communication with administration or internal transfers. Access follows authorisation and business necessity.',
        ],
      },
      {
        heading: 'Age',
        paragraphs: [
          `${PRODUCT_EN} is intended only for users aged 18 or older. We do not knowingly collect personal data from children.`,
        ],
      },
      {
        heading: 'Orders and payment',
        paragraphs: [
          'You may order physical oil and related products. There is no online payment, Apple Pay, Google Pay, or card processing. An authorised agency employee fulfils or delivers the order and collects payment upon delivery.',
          'These are physical goods, not digital purchases or in-app purchases.',
        ],
      },
      {
        heading: 'Marketing and reminders',
        paragraphs: [
          `${PRODUCT_EN} does not currently send marketing campaigns or promotional messages. Operational notifications, whether shown in the app or sent as push notifications, are not marketing. Any future marketing would require an updated policy and appropriate consent before activation.`,
          'Maintenance reminders are currently shown only inside the application, as an in-app indicator when an oil-change or service date is approaching. They are not sent as push notifications.',
        ],
      },
      {
        heading: 'User-generated public content',
        paragraphs: [
          'There is currently no public user-generated content system: no public reviews, comments, social posts, public photo feed, or public user-to-user chat.',
        ],
      },
      {
        heading: 'Your rights and access',
        paragraphs: [
          `You can edit your account information inside the app. You may also email ${SUPPORT_EMAIL} to request information about your personal data, correction where applicable, a copy of your personal data, account or data deletion, or privacy questions.`,
          'There is no automated data-export feature at this time. Manual handling through the official email is the current process.',
        ],
      },
      {
        heading: 'Retention and account deletion',
        paragraphs: [
          `You can delete your account inside the ${PRODUCT_EN} app or at ${WEBSITE}/account-deletion using your existing credentials. Deletion is account deletion, not deactivation.`,
          'Once deletion is finally confirmed, account access stops immediately and active sessions are revoked. Personal data on the live system is erased or anonymised immediately. Residual copies in backups or operational systems are removed or overwritten within up to 30 days.',
          'You cannot delete the account while an unsettled internal wallet balance remains, while a vehicle transfer is pending, or if you are the last administrator. Those blockers must be resolved first.',
          'Certain records may be retained when legitimately required for completed orders, invoices, debts or receivables, inventory or accounting integrity, fraud or security prevention, legal or regulatory obligations, or proof of completed commercial transactions. Retained records are limited to what is necessary and are not reused for unrelated purposes. A deleted account’s published workshop or store listing is removed from the live directory; related commercial records may remain.',
          'If your account has an email address, a transactional confirmation is sent after the deletion request. Phone-only accounts do not require email to complete deletion.',
        ],
      },
      {
        heading: 'Stopping use',
        paragraphs: [
          `If you no longer wish to use ${PRODUCT_EN} under this Privacy Policy and the Terms of Use, you may stop using the service and delete your account. Deletion does not erase records that must legitimately be retained as described above.`,
        ],
      },
      {
        heading: 'Changes',
        paragraphs: [
          'If we make a material change that requires a new Privacy Policy version, signed-in users will be asked to accept the updated document before continuing to use the app. Account deletion and access to these legal pages remain available without accepting the new version.',
          `Questions: ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
    ar: [
      {
        heading: 'من نحن',
        paragraphs: [
          `${PRODUCT_AR} (${PRODUCT_EN}) تشغّله ${OPERATOR_AR}، وصاحبه والوكيل الحصري هو ${OWNER_AR}. العنوان المسجّل: ${ADDRESS_AR}. رقم هاتف المالك ${OWNER_PHONE} هو للتعريف بالأعمال فقط، وليس قناة رسمية للخصوصية أو الدعم أو حذف الحساب.`,
          `الموقع الرسمي هو ${WEBSITE}. تُرسل طلبات الخصوصية والبيانات والدعم وحذف الحساب إلى ${SUPPORT_EMAIL}.`,
          `${DEVELOPER_AR} طوّرت البرنامج. وهي ليست مالكة خدمة ${PRODUCT_AR} ولا مشغّلتها.`,
        ],
      },
      {
        heading: 'تصف هذه السياسة السلوك الحالي في الإنتاج',
        paragraphs: [
          `سياسة الخصوصية الإصدار ${PRIVACY_VERSION} (سارية من ${PRIVACY_EFFECTIVE}) تصف كيف يعمل ${PRODUCT_AR} اليوم. ولا تصف ميزات مستقبلية وكأنها مفعّلة.`,
          `يتطلب ${PRODUCT_AR} حالياً حساباً لاستخدام الخدمة.`,
          'يضيف الإصدار 1.1 الإشعارات الفورية الموضحة أدناه. ولا تصلك إلا في إصدار من تطبيق الهاتف يدعمها، وفقط إذا فعّلتها.',
        ],
      },
      {
        heading: 'بيانات الحساب والبيانات الشخصية',
        paragraphs: [
          'قد تشمل بيانات الحساب: الاسم، رقم الهاتف المحمول، عنوان البريد الإلكتروني إن قدّمته، المحافظة التي تختارها يدوياً، صورة الملف الشخصي، نوع الحساب أو دوره، ومعلومات المصادقة.',
          'قد يُستخدم رقم الهاتف المحمول معرّفاً فريداً للحساب وتسجيل الدخول. البريد الإلكتروني اختياري.',
          `لا يرسل ${PRODUCT_AR} رسائل SMS، ولا يستخدم رموز تحقق عبر SMS، ولا يتتبع أرقام الهواتف بما يتجاوز حفظ الرقم الذي تسجّله كمعرّف حساب.`,
          'تُحفظ كلمات المرور كتجزئات آمنة فقط. ولا يمكن للموظفين قراءة كلمة المرور الأصلية.',
        ],
      },
      {
        heading: 'بيانات المركبات',
        paragraphs: [
          'يمكنك حفظ تفاصيل المركبة وصورها ومعلومات الصيانة أو الخدمة للمركبات التي تديرها.',
          'يمكنك ربط مركبة بورشة. هذا الربط علاقة خدمة داخلية تفيد بأن الورشة تخدم تلك المركبة. ولا يمنح الورشة وصولاً غير مقيّد إلى حسابك الخاص أو إلى كل معلومات المركبة. يرى موظفو الورشة فقط ما يُصرَّح لهم باستخدامه لتلك العلاقة.',
        ],
      },
      {
        heading: 'الورش والمتاجر',
        paragraphs: [
          `يتضمن ${PRODUCT_AR} دليل أعمال للورش والمتاجر. قد تشمل المعلومات المنشورة اسم النشاط ووصفه والمحافظة والمنطقة والعنوان النصي وساعات العمل والخدمات وهاتف العمل وواتساب إن وُفّر، ومعلومات أخرى ينشرها النشاط عمداً.`,
          'هذا دليل أعمال. ولا يُنشر الملف الشخصي الخاص للمالك كمحتوى عام في الدليل.',
        ],
      },
      {
        heading: 'الموقع',
        paragraphs: [
          `لا يجمع ${PRODUCT_AR} موقعك الجغرافي أو GPS ولا يطلبه. تختار المحافظة يدوياً. واكتشاف الورش والمتاجر يعتمد على هذا الاختيار، لا على خدمات موقع الجهاز.`,
        ],
      },
      {
        heading: 'الصور والكاميرا',
        paragraphs: [
          'قد يسمح التطبيق برفع صورة ملف شخصي وصور مركبات وصور منتجات أو مخزون، والتقاط صورة حيث يدعم الجهاز ذلك، ومسح رموز QR أو الباركود.',
          'يُستخدم الوصول إلى الكاميرا والصور لهذه الوظائف فقط. يفضّل التطبيق منتقي صور النظام. ولا يطلب وصولاً واسعاً دائماً إلى مكتبة صورك بالكامل.',
        ],
      },
      {
        heading: 'الإشعارات الفورية',
        paragraphs: [
          `إذا فعّلتها، يرسل تطبيق ${PRODUCT_AR} إشعارات فورية عن أحداث تخص حسابك. وهي إشعارات تشغيلية للخدمة وليست إعلانات. لا تبدأ الإشعارات الفورية إلا عندما تختار تفعيلها في التطبيق، وحيث يتطلب جهازك إذناً يطلبه التطبيق في تلك اللحظة. ويمكنك إيقافها في أي وقت من إعدادات جهازك.`,
          'تشمل الإشعارات الفورية حالياً: اكتمال نقل ملكية مركبة، أو نقل مركبة إلى حسابك أو منه بواسطة فريق الدعم؛ تسجيل خدمة أو إصدار فاتورة لمركبتك من قبل ورشة؛ قراراً بشأن ورشتك أو متجرك (الموافقة أو الرفض أو الإيقاف)؛ تغيّر حالة طلباتك؛ استلام مبلغ في محفظتك الداخلية؛ نشر إعلانات سيارتك في السوق أو رفضها أو إيقافها أو إغلاقها بعد نقل الملكية، والاستفسارات عنها؛ ورسائل يكتبها فريق الكار كارنا لحسابك بشكل فردي. ويظهر كل إشعار فوري أيضاً في قائمة الإشعارات داخل التطبيق، ويمكن إيقاف بعض الأنواع من تفضيلات الإشعارات في التطبيق.',
          'لإيصالها، يحصل التطبيق على رمز إشعارات لجهازك ويرسله إلى خوادمنا مع منصة الجهاز (iOS أو Android) وإصدار التطبيق. ونحفظ الرمز مرتبطاً بحسابك وبالجلسة التي سجّلت الدخول بها، كي تصل الإشعارات إلى الشخص الصحيح على الجهاز الصحيح. ولا نجمع لهذا الغرض معرّفات إعلانية أو معرّفات عتاد الجهاز.',
          'يحتوي الإشعار الفوري على عنوان ونص قصيرين، وقدر صغير من المعلومات التقنية يستخدمها التطبيق لفتح الشاشة المعنية، مثل رقم الإشعار ورقم المركبة أو الطلب أو الفاتورة المرتبطة. وقد يذكر النص مركبة (الصانع والطراز والسنة) أو ورشة أو متجراً أو رقم طلب أو فاتورة أو سبب قرار ما. وفي تحويلات المحفظة لا يُظهر الإشعار المبلغ ولا اسم الطرف الآخر؛ فهذه التفاصيل تظهر داخل التطبيق فقط.',
          'يتوقف الجهاز عن تلقي الإشعارات الفورية لحسابك عند تسجيل خروجك منه، أو عند تسجيل خروجك من جميع الأجهزة، أو عند إعادة تعيين كلمة المرور، أو عند تعطيل حسابك أو حذفه، أو عند انتهاء صلاحية الجلسة. وإذا أفادت خدمة الإشعارات بأن جهازاً لم يعد قادراً على تلقيها، كأن يكون التطبيق قد حُذف، نتوقف عن الإرسال إليه. وإذا تعذّر وصول تسجيل الخروج إلى خوادمنا، يكمله التطبيق لاحقاً، ودائماً قبل أن يسجّل أي شخص آخر الدخول على ذلك الجهاز.',
          'نحتفظ لكل إشعار فوري بمعلومات التسليم اللازمة لتشخيص المشكلات: هل قُبل ومُرِّر إلى Apple أو Google، والمنصة، وأي خطأ أُبلغ عنه. وتُحفظ مع الإشعار وتُحذف معه عند حذف حسابك.',
        ],
      },
      {
        heading: 'التحليلات والإعلانات والتتبع',
        paragraphs: [
          `لا يستخدم ${PRODUCT_AR} حالياً حزم تحليلات أو تتبعاً إعلانياً أو تتبعاً سلوكياً أو حزم إعلانات أو Meta Pixel أو Google Analytics أو Firebase Analytics.`,
          'لا تغيّر الإشعارات الفورية ذلك: تُستخدم الخدمات المذكورة أدناه لإيصالها فقط.',
        ],
      },
      {
        heading: 'مقدمو الخدمات',
        paragraphs: [
          'يُرسل البريد التشغيلي (التحقق من البريد حيث ينطبق، واستعادة كلمة المرور) عبر Mailgun كمعالج/مقدم خدمة. يعالج Mailgun عنوان البريد ومحتوى تلك الرسائل لتسليمها فقط.',
          'تُسلَّم تحديثات التطبيق الأصلي عبر الهواء بواسطة Expo (EAS Update). بعد التشغيل قد يتصل التطبيق بخدمة تحديثات Expo لتنزيل تحديث. تعالج Expo بيانات الطلب الاعتيادية مثل عنوان IP ومعلومات المنصة أو بيئة التشغيل لهذا الغرض. الموقع العام لا يستخدم Expo.',
          'تُسلَّم الإشعارات الفورية عبر خدمة إشعارات Expo، التي تمررها إلى خدمة إشعارات Apple (APNs) على iOS، وإلى Firebase Cloud Messaging (FCM)، وهي خدمة من Google، على Android. تعالج هذه الجهات رمز الإشعارات، وعنوان الإشعار ونصه ومعلوماته التقنية، ومعلومات التوجيه اللازمة لإيصاله. ولإصدار رمز الإشعارات تتلقى Expo أيضاً الرمز الذي تخصصه Apple أو Google للتطبيق على جهازك، ومعرّف تثبيت يولّده التطبيق، ومعرّف التطبيق. وعلى Android يسجّل مكوّن الرسائل في التطبيق الجهاز لدى Google لتلقي الرسائل. تُستخدم Firebase Cloud Messaging لإيصال الإشعارات فقط، وهي ليست Firebase Analytics.',
          `لا يستخدم ${PRODUCT_AR} حالياً تيليغرام للإشعارات أو ربط الحساب.`,
          'تُستضاف بيانات الخادم في ألمانيا عبر بنية STRATO. لذلك تُعالَج البيانات الشخصية وتُحفظ خارج سوريا. ولا نقدّم ضمانات قانونية غير مدعومة بشأن النقل عبر الحدود أبعد من وصف هذا الاستضافة.',
        ],
      },
      {
        heading: 'المشاركة',
        paragraphs: [
          `لا يبيع ${PRODUCT_AR} البيانات الشخصية للمستخدمين.`,
          'قد يُطلَع على البيانات الشخصية أو تُشارك فقط بقدر ما يلزم لتقديم الخدمة المطلوبة، أو لتشغيل سير العمل التجاري المصرّح به، أو مع مقدمي الخدمات/المعالجين، أو للوفاء بالتزامات قانونية أو أمنية.',
          'صور الملف الشخصي ليست عامة عموماً. قد تظهر صورة صغيرة في سير عمل مشروع مثل التواصل مع الإدارة أو التحويلات الداخلية. ويتبع الوصول التفويض والضرورة التجارية.',
        ],
      },
      {
        heading: 'العمر',
        paragraphs: [`الخدمة مخصّصة لمن تبلغ أعمارهم 18 سنة فأكثر. ولا نجمع عن علم بيانات أطفال.`],
      },
      {
        heading: 'الطلبات والدفع',
        paragraphs: [
          'يمكنك طلب زيوت ومنتجات مادية ذات صلة. لا يوجد دفع إلكتروني ولا Apple Pay ولا Google Pay ولا معالجة بطاقات. يُنفّذ الطلب أو يُسلَّم بواسطة موظف مخوّل من الوكالة ويُحصَّل المبلغ عند التسليم.',
          'هذه سلع مادية وليست مشتريات رقمية أو مشتريات داخل التطبيق.',
        ],
      },
      {
        heading: 'التسويق والتذكيرات',
        paragraphs: [
          `لا يرسل ${PRODUCT_AR} حالياً حملات تسويقية أو رسائل ترويجية. الإشعارات التشغيلية، سواء ظهرت داخل التطبيق أو أُرسلت كإشعارات فورية، ليست تسويقاً. وأي تسويق مستقبلي يتطلب تحديث السياسة وموافقة مناسبة قبل التفعيل.`,
          'تظهر تذكيرات الصيانة حالياً داخل التطبيق فقط، كمؤشر عند اقتراب موعد تبديل الزيت أو الخدمة. ولا تُرسل كإشعارات فورية.',
        ],
      },
      {
        heading: 'المحتوى العام الذي ينشئه المستخدمون',
        paragraphs: [
          'لا يوجد حالياً نظام محتوى عام ينشئه المستخدمون: لا تقييمات عامة ولا تعليقات ولا منشورات اجتماعية ولا موجز صور عام ولا دردشة عامة بين المستخدمين.',
        ],
      },
      {
        heading: 'حقوقك والوصول إلى بياناتك',
        paragraphs: [
          `يمكنك تعديل معلومات حسابك داخل التطبيق. ويمكنك أيضاً مراسلة ${SUPPORT_EMAIL} لطلب معلومات عن بياناتك الشخصية، أو تصحيحها حيث ينطبق، أو نسخة منها، أو حذف الحساب أو البيانات، أو أسئلة الخصوصية.`,
          'لا توجد ميزة تصدير آلي للبيانات حالياً. المعالجة اليدوية عبر البريد الرسمي هي الإجراء الحالي.',
        ],
      },
      {
        heading: 'الاحتفاظ وحذف الحساب',
        paragraphs: [
          `يمكنك حذف حسابك داخل تطبيق ${PRODUCT_AR} أو عبر ${WEBSITE}/account-deletion باستخدام بيانات دخولك الحالية. الحذف هو حذف حساب وليس مجرد تعطيل.`,
          'بعد التأكيد النهائي يتوقف الوصول فوراً وتُلغى الجلسات النشطة. تُمحى البيانات الشخصية أو تُجهَّل فوراً على النظام الحي. وتُزال النسخ المتبقية في النسخ الاحتياطية أو الأنظمة التشغيلية أو تُستبدل خلال مدة تصل إلى 30 يوماً.',
          'لا يمكن حذف الحساب إذا بقي رصيد محفظة داخلية غير مسوّى، أو تحويل مركبة معلّق، أو إذا كنت آخر مدير. يجب حل هذه الموانع أولاً.',
          'قد تُحفظ سجلات معيّنة عند لزومها قانوناً أو تجارياً للطلبات المكتملة والفواتير والديون والمقبوضات وسلامة المخزون أو المحاسبة ومنع الاحتيال أو الأمن والالتزامات القانونية وإثبات المعاملات التجارية المكتملة. وتقتصر السجلات المحتفظ بها على ما يلزم ولا تُعاد استخدامها لأغراض غير ذات صلة. تُزال قائمة الورشة أو المتجر المنشورة من الدليل الحي؛ وقد تبقى السجلات التجارية المرتبطة.',
          'إذا كان للحساب بريد إلكتروني تُرسل رسالة تأكيد تشغيلية بعد طلب الحذف. وحسابات الهاتف فقط لا تحتاج بريداً لإكمال الحذف.',
        ],
      },
      {
        heading: 'التوقف عن الاستخدام',
        paragraphs: [
          `إذا لم تعد ترغب في استخدام ${PRODUCT_AR} وفق سياسة الخصوصية وشروط الاستخدام، يمكنك التوقف عن الاستخدام وحذف حسابك. والحذف لا يمحو السجلات التي يجب الاحتفاظ بها كما هو موضح أعلاه.`,
        ],
      },
      {
        heading: 'التغييرات',
        paragraphs: [
          'إذا أجرينا تغييراً جوهرياً يتطلب إصداراً جديداً من سياسة الخصوصية، سيُطلب من المستخدمين المسجّلين قبول المستند المحدّث قبل متابعة استخدام التطبيق. ويبقى حذف الحساب وهذه الصفحات القانونية متاحين دون قبول الإصدار الجديد.',
          `للأسئلة: ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
  },
};

export const termsOfUse: LegalDocument = {
  type: 'terms_of_use',
  version: TERMS_VERSION,
  effectiveDate: TERMS_EFFECTIVE,
  lastUpdated: TERMS_EFFECTIVE,
  title: {
    ar: `شروط الاستخدام — ${PRODUCT_AR}`,
    en: `Terms of Use — ${PRODUCT_EN}`,
  },
  sections: {
    en: [
      {
        heading: 'The service',
        paragraphs: [
          `${PRODUCT_EN} (${PRODUCT_AR}) is operated by ${OPERATOR_EN} (${OWNER_EN}). Address: ${ADDRESS_EN}. Official contact for these Terms: ${SUPPORT_EMAIL}. Website: ${WEBSITE}.`,
          `${DEVELOPER_EN} developed the software and is not the owner or operator of the service.`,
          `These Terms of Use version ${TERMS_VERSION} are effective ${TERMS_EFFECTIVE}.`,
        ],
      },
      {
        heading: 'Eligibility',
        paragraphs: [
          `You must be 18 years of age or older to create an account or use ${PRODUCT_EN}.`,
        ],
      },
      {
        heading: 'Accounts',
        paragraphs: [
          'You must provide accurate information and keep your credentials confidential. You are responsible for activity that occurs with your credentials.',
          'The mobile phone number may be your login identifier. Email is optional.',
          'Workshop and store accounts are business accounts. Agency administration reviews and may approve or reject those applications. Employee accounts are created or authorised by administration.',
        ],
      },
      {
        heading: 'Permitted and prohibited use',
        paragraphs: [
          `Use ${PRODUCT_EN} only for its intended vehicle-service, directory, and oil-agency purposes.`,
          'You must not misuse the service, attempt unauthorised access, impersonate others, interfere with security, or use the service for unlawful activity.',
        ],
      },
      {
        heading: 'Vehicles and workshops',
        paragraphs: [
          'Associating a vehicle with a workshop records an internal service relationship. It does not grant the workshop unrestricted access to your private account.',
        ],
      },
      {
        heading: 'Physical product orders',
        paragraphs: [
          'Orders are for physical oil and related products. There is no online payment, Apple Pay, Google Pay, or card processing. Payment is collected upon delivery by the agency or an authorised employee.',
          'Delivery fees are not necessarily fixed. They may be determined by the agency employee based on the order, area, and delivery circumstances. The applicable delivery cost must be communicated clearly to the customer before final fulfilment or confirmation where appropriate.',
          'An order may be cancelled before preparation begins. After delivery, returns depend on product condition and agency approval. Opened or used products cannot be returned.',
        ],
      },
      {
        heading: 'Internal wallet and rewards',
        paragraphs: [
          `The ${PRODUCT_EN} wallet is an internal accounting balance only. It is not a bank account, electronic money, a payment service, a withdrawable cash wallet, or a bank-transfer mechanism. It is used for internal financial relationships between the oil agency and workshops or businesses.`,
          'Reward points may be redeemed for eligible gifts or benefits. They have no withdrawable cash value, cannot be transferred to a bank account, and cannot be exchanged for cash unless a future written programme expressly states otherwise.',
        ],
      },
      {
        heading: 'Intellectual property and availability',
        paragraphs: [
          `The ${PRODUCT_EN} name, branding, and software are owned by the operator or licensed to it. You may not copy or reuse them except as needed to use the service.`,
          'The service may be interrupted for maintenance, connectivity, or circumstances outside our reasonable control. We will take reasonable care but do not guarantee uninterrupted availability.',
        ],
      },
      {
        heading: 'Limitation of liability',
        paragraphs: [
          'To the extent permitted by applicable law, the operator is not liable for indirect or consequential losses arising from use of the service. Nothing in these Terms is intended to exclude liability that cannot lawfully be excluded under Syrian law. Provisions that may require counsel under Syrian law are stated in neutral terms and remain subject to later legal review.',
        ],
      },
      {
        heading: 'Suspension and deletion',
        paragraphs: [
          'We may suspend an account for abuse, security reasons, or legal obligations.',
          `You may delete your account in the app or at ${WEBSITE}/account-deletion. Access stops immediately after final confirmation. Personal data is erased or anonymised as described in the Privacy Policy. Records that must legitimately be retained are not erased.`,
          'If you no longer accept these Terms or the Privacy Policy, stop using the service and delete your account.',
        ],
      },
      {
        heading: 'Changes and governing law',
        paragraphs: [
          'We may update these Terms. Material updates that require a new version will ask signed-in users to accept before continuing. Account deletion and these legal pages remain available without accepting the new version.',
          'These Terms are governed by Syrian law.',
          `Contact: ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
    ar: [
      {
        heading: 'الخدمة',
        paragraphs: [
          `${PRODUCT_AR} (${PRODUCT_EN}) تشغّله ${OPERATOR_AR} (${OWNER_AR}). العنوان: ${ADDRESS_AR}. جهة الاتصال الرسمية لهذه الشروط: ${SUPPORT_EMAIL}. الموقع: ${WEBSITE}.`,
          `${DEVELOPER_AR} طوّرت البرنامج وليست مالكة الخدمة ولا مشغّلتها.`,
          `شروط الاستخدام الإصدار ${TERMS_VERSION} سارية من ${TERMS_EFFECTIVE}.`,
        ],
      },
      {
        heading: 'الأهلية',
        paragraphs: [`يجب أن تبلغ 18 سنة فأكثر لإنشاء حساب أو استخدام ${PRODUCT_AR}.`],
      },
      {
        heading: 'الحسابات',
        paragraphs: [
          'يجب تقديم معلومات صحيحة والحفاظ على سرية بيانات الدخول. وأنت مسؤول عن النشاط الذي يتم ببياناتك.',
          'قد يكون رقم الهاتف معرّف تسجيل الدخول. والبريد الإلكتروني اختياري.',
          'حسابات الورش والمتاجر حسابات أعمال. وتراجع إدارة الوكالة تلك الطلبات وقد توافق عليها أو ترفضها. وحسابات الموظفين تُنشأ أو تُصرَّح من الإدارة.',
        ],
      },
      {
        heading: 'الاستخدام المسموح والممنوع',
        paragraphs: [
          `استخدم ${PRODUCT_AR} فقط لأغراض خدمة المركبات والدليل ووكالة الزيوت المقصودة.`,
          'يحظر إساءة استخدام الخدمة أو محاولة الوصول غير المصرّح به أو انتحال الغير أو الإضرار بالأمن أو الاستخدام غير المشروع.',
        ],
      },
      {
        heading: 'المركبات والورش',
        paragraphs: [
          'ربط مركبة بورشة يسجّل علاقة خدمة داخلية. ولا يمنح الورشة وصولاً غير مقيّد إلى حسابك الخاص.',
        ],
      },
      {
        heading: 'طلبات المنتجات المادية',
        paragraphs: [
          'الطلبات تخص زيوتاً ومنتجات مادية ذات صلة. لا يوجد دفع إلكتروني ولا Apple Pay ولا Google Pay ولا معالجة بطاقات. ويُحصَّل المبلغ عند التسليم بواسطة الوكالة أو موظف مخوّل.',
          'رسوم التوصيل ليست بالضرورة ثابتة. وقد يحدّدها موظف الوكالة بحسب الطلب والمنطقة وظروف التسليم. ويجب إبلاغ العميل بتكلفة التوصيل بوضوح قبل التنفيذ أو التأكيد النهائي حيث يناسب.',
          'يمكن إلغاء الطلب قبل بدء التجهيز. وبعد التسليم تعتمد الإرجاعات على حالة المنتج وموافقة الوكالة. ولا يمكن إرجاع المنتجات المفتوحة أو المستعملة.',
        ],
      },
      {
        heading: 'المحفظة الداخلية والمكافآت',
        paragraphs: [
          `محفظة ${PRODUCT_AR} رصيد محاسبي داخلي فقط. وهي ليست حساباً بنكياً ولا نقوداً إلكترونية ولا خدمة دفع ولا محفظة نقدية قابلة للسحب ولا آلية تحويل بنكي. وتُستخدم للعلاقات المالية الداخلية بين وكالة الزيوت والورش أو الأعمال.`,
          'يمكن استبدال نقاط المكافآت بهدايا أو منافع مؤهّلة. وليس لها قيمة نقدية قابلة للسحب، ولا يمكن تحويلها إلى حساب بنكي، ولا مبادلتها نقداً ما لم ينص برنامج مكتوب مستقبلي على خلاف ذلك صراحة.',
        ],
      },
      {
        heading: 'الملكية الفكرية والتوافر',
        paragraphs: [
          `اسم ${PRODUCT_AR} وهويته والبرنامج ملك للمشغّل أو مرخّصة له. ولا يجوز نسخها أو إعادة استخدامها إلا بالقدر اللازم لاستخدام الخدمة.`,
          'قد تنقطع الخدمة للصيانة أو الاتصال أو ظروف خارجة عن السيطرة المعقولة. نبذل عناية معقولة ولا نضمن توافراً دون انقطاع.',
        ],
      },
      {
        heading: 'حدود المسؤولية',
        paragraphs: [
          'في حدود ما يسمح به القانون الواجب التطبيق، لا يُسأل المشغّل عن الخسائر غير المباشرة أو التبعية الناشئة عن استخدام الخدمة. ولا يُقصد من هذه الشروط استبعاد مسؤولية لا يجوز استبعادها قانوناً بموجب القانون السوري. والأحكام التي قد تحتاج رأياً قانونياً بموجب القانون السوري صيغت بعبارات محايدة وتبقى خاضعة لمراجعة قانونية لاحقة.',
        ],
      },
      {
        heading: 'التعليق والحذف',
        paragraphs: [
          'يجوز تعليق الحساب بسبب الإساءة أو الأمن أو الالتزامات القانونية.',
          `يمكنك حذف حسابك في التطبيق أو عبر ${WEBSITE}/account-deletion. يتوقف الوصول فوراً بعد التأكيد النهائي. وتُمحى البيانات الشخصية أو تُجهَّل كما في سياسة الخصوصية. ولا تُمحى السجلات التي يجب الاحتفاظ بها مشروعاً.`,
          'إذا لم تعد تقبل هذه الشروط أو سياسة الخصوصية، توقف عن الاستخدام واحذف حسابك.',
        ],
      },
      {
        heading: 'التغييرات والقانون الحاكم',
        paragraphs: [
          'يجوز تحديث هذه الشروط. والتحديثات الجوهرية التي تتطلب إصداراً جديداً تطلب من المستخدمين المسجّلين القبول قبل المتابعة. ويبقى حذف الحساب وهذه الصفحات القانونية متاحين دون قبول الإصدار الجديد.',
          'تخضع هذه الشروط للقانون السوري.',
          `للتواصل: ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
  },
};
